import React from 'react';
import styled from '@emotion/styled';
import { AnimatePresence, LayoutGroup, motion } from 'framer-motion';
import type { TextProps } from '@hotjar/hotsauce';
import { tokens, Text } from '@hotjar/hotsauce';

const DOT_SIZE = 12;
const DOT_BORDER = 8;

const ANIMATION_VARIANTS = {
  enter: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};

const StepsWrapper = styled.div`
  margin-bottom: ${tokens.spacer.x8};
`;

const StepWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${tokens.spacer.x1};
  margin-bottom: ${tokens.spacer.x3pt5};
  position: relative;
`;

const LastStepWrapper = styled.div<{ isFunnelStep?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 70px;
  gap: ${tokens.spacer.x2};
  position: relative;
  background: white;
  box-sizing: content-box;
`;

const VerticalLine = styled.div<{
  isFirstChain?: boolean;
  isLastChain?: boolean;
}>`
  content: ' ';
  display: block;
  position: absolute;
  top: ${(props) => (props.isFirstChain ? '50%' : '0')};
  left: ${(DOT_SIZE + DOT_BORDER * 2) / 2 - 1}px;
  background-color: ${tokens.color.ui_disabled};
  width: 2px;
  height: ${(props) =>
    props.isLastChain
      ? '50%'
      : `calc(${props.isFirstChain ? '50%' : '100%'} + ${tokens.spacer.x3pt5})`};
`;

const StepBox = styled.div<{ isInvalid?: boolean }>`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  border: 1px solid
    ${({ isInvalid }) => (isInvalid ? tokens.color.ui_border_danger : tokens.color.ui_border_light)};
  padding: ${tokens.spacer.x2} ${tokens.spacer.x7} ${tokens.spacer.x2} ${tokens.spacer.x3};
  border-radius: ${tokens.radius.l};
  gap: ${tokens.spacer.x2};
  min-width: 0;
`;

const TextMarker = styled.span`
  border: ${tokens.spacer.x2} solid ${tokens.color.white};
  background-color: ${tokens.color.white};
  position: relative;
  margin-left: -6px;
`;

const Dot = styled.div<{ backgroundColor?: string }>`
  height: ${DOT_SIZE + DOT_BORDER * 2}px;
  width: ${DOT_SIZE + DOT_BORDER * 2}px;
  border-radius: 50%;
  background-color: ${(props) => props.backgroundColor || tokens.color.ui_disabled};
  border: ${DOT_BORDER}px solid white;
  position: relative;
`;

const ErrorText = styled(Text)`
  position: absolute;
  bottom: -${tokens.spacer.x2pt5};
  left: ${tokens.spacer.x8pt5};
`;

export type StepMarker =
  | { kind: 'text'; textValue: string; textColor?: TextProps['textColor'] }
  | { kind: 'dot'; color?: string };

interface StepProps {
  children: React.ReactNode;
  stepMarker: StepMarker;
  isFirstChain?: boolean;
  isLastChain?: boolean;
  isInvalid?: boolean;
  errorText?: string;
}

interface StepMarkerProps {
  stepMarker: StepMarker;
}

const StepMarker = ({ stepMarker }: StepMarkerProps): JSX.Element | null => {
  if (stepMarker.kind === 'text') {
    return (
      <TextMarker>
        <Text textType="subtitle1" textColor={stepMarker.textColor}>
          {stepMarker.textValue}
        </Text>
      </TextMarker>
    );
  }
  if (stepMarker.kind === 'dot') {
    return <Dot backgroundColor={stepMarker.color} />;
  }

  return null;
};

const Step = ({
  children,
  isFirstChain = false,
  isLastChain = false,
  isInvalid = false,
  errorText,
  stepMarker,
}: StepProps) => {
  return (
    <motion.div layout variants={ANIMATION_VARIANTS} initial="enter" animate="animate" exit="exit">
      <StepWrapper>
        {!(isFirstChain && isLastChain) && (
          <VerticalLine isFirstChain={isFirstChain} isLastChain={isLastChain} />
        )}
        <StepMarker stepMarker={stepMarker} />
        <StepBox isInvalid={isInvalid}>{children}</StepBox>
        {isInvalid && errorText && (
          <ErrorText textType="caption" textColor="text_danger">
            {errorText}
          </ErrorText>
        )}
      </StepWrapper>
    </motion.div>
  );
};

const AddNewStep = ({
  children,
  stepMarker,
  isFunnelStep,
}: Omit<StepProps, 'isFirstChain' | 'isLastChain'> & { isFunnelStep?: boolean }) => {
  return (
    <motion.div layout variants={ANIMATION_VARIANTS} initial="enter" animate="animate" exit="exit">
      <LastStepWrapper isFunnelStep={isFunnelStep}>
        <VerticalLine isLastChain />
        <StepMarker stepMarker={stepMarker} />
        {children}
      </LastStepWrapper>
    </motion.div>
  );
};

export const Steps = ({ children }: { children: React.ReactNode }): JSX.Element => {
  return (
    <StepsWrapper>
      <AnimatePresence initial={false}>
        <LayoutGroup>{children}</LayoutGroup>
      </AnimatePresence>
    </StepsWrapper>
  );
};

Steps.Step = Step;
Steps.AddNewStep = AddNewStep;
